import anime from 'animejs';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');


// ============================================================================
// HEADER ANIMATION
// ============================================================================

function initHeaderAnimation() {
  const headerLogo = document.getElementById('header-logo');
  if (!headerLogo) return;

  anime({
    targets: headerLogo,
    scale: [1, 1.08, 1],
    opacity: [1, 0.9, 1],
    duration: 2800,
    easing: 'easeInOutSine',
    loop: true
  });
}

// ============================================================================
// DEMO PANEL
// ============================================================================

function initDemoPanel() {
  const demoPanel = document.getElementById('demo-panel');
  const demoToggle = document.getElementById('demo-toggle');
  const speedDemoBtn = document.getElementById('speed-demo-btn');
  const speedDemoStatus = document.getElementById('speed-demo-status');

  if (!demoPanel || !demoToggle) return;

  const setExpanded = (isOpen) => {
    demoToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  demoPanel.classList.add('ready');
  setExpanded(false);

  const openDemoPanel = () => {
    demoPanel.classList.add('open');
    setExpanded(true);

    if (speedDemoStatus) {
      speedDemoStatus.textContent = 'Demo panel opened.';
    }

    demoPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstAction = demoPanel.querySelector('.demo-content a, .demo-content button');
    if (firstAction) {
      firstAction.focus({ preventScroll: true });
    }
  };

  demoToggle.addEventListener('click', () => {
    demoPanel.classList.toggle('open');
    const isOpen = demoPanel.classList.contains('open');
    setExpanded(isOpen);
  });

  if (speedDemoBtn) {
    speedDemoBtn.addEventListener('click', openDemoPanel);
  }
}

// ============================================================================
// SCROLL EFFECTS
// ============================================================================

function initScrollEffects() {
  const sections = document.querySelectorAll('.section');

  if (!sections.length) {
    return;
  }

  function revealVisibleSections() {
    sections.forEach((section, index) => {
      if (index === 0) {
        section.classList.add('section--visible');
        return;
      }
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.95 && rect.bottom > 0) {
        section.classList.add('section--visible');
      }
    });

    if (window.location.hash) {
      try {
        const target = document.querySelector(window.location.hash);
        const parentSection = target ? (target.matches('.section') ? target : target.closest('.section')) : null;
        if (parentSection) {
          parentSection.classList.add('section--visible');
          parentSection.querySelectorAll('.feature').forEach(f => {
            f.style.opacity = '1';
            f.style.transform = 'none';
          });
        }
      } catch (e) {}
    }
  }

  if (prefersReducedMotion.matches) {
    sections.forEach(section => section.classList.add('section--visible'));
    document.querySelectorAll('.feature').forEach(feature => {
      feature.style.opacity = '1';
      feature.style.transform = 'none';
    });
    return;
  }

  revealVisibleSections();
  window.addEventListener('hashchange', revealVisibleSections);

  sections.forEach((section, index) => {
    if (index === 0) {
      section.classList.add('section--visible');
      return;
    }

    ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      onEnter: () => section.classList.add('section--visible'),
      onEnterBack: () => section.classList.add('section--visible')
    });
  });

  gsap.utils.toArray('.feature').forEach((feature) => {
    gsap.fromTo(feature,
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: feature,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });

  ScrollTrigger.refresh();
}

// ============================================================================
// SMOOTH SCROLL & MOBILE OPTIMIZATIONS
// ============================================================================

function initSmoothScroll() {
  if ('ontouchstart' in window) {
    document.body.style.overscrollBehavior = 'none';
  }
}

// ============================================================================
// DEMO BUTTON INTERACTIONS
// ============================================================================

function initDemoButtons() {
  const demoButtons = document.querySelectorAll('.demo-button, .demo-button-large');
  demoButtons.forEach(button => {
    const icon = button.querySelector('.demo-icon, .demo-icon-large');

    button.addEventListener('mouseenter', () => {
      if (!icon || prefersReducedMotion.matches) return;
      anime({
        targets: icon,
        scale: [1, 1.15, 1],
        duration: 400,
        easing: 'easeOutElastic(1, .6)'
      });
    });

    button.addEventListener('click', (event) => {
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const ripple = document.createElement('span');

      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;
      ripple.style.position = 'absolute';
      ripple.style.borderRadius = '50%';
      ripple.style.background = 'rgba(255, 255, 255, 0.3)';
      ripple.style.pointerEvents = 'none';
      ripple.style.transform = 'scale(0)';

      button.style.position = 'relative';
      button.style.overflow = 'hidden';
      button.appendChild(ripple);

      anime({
        targets: ripple,
        scale: [0, 2.2],
        opacity: [0.5, 0],
        duration: 600,
        easing: 'easeOutQuad',
        complete: () => ripple.remove()
      });

    });
  });
}

// ============================================================================
// HERO CTA SCROLL
// ============================================================================

function initHeroCTA() {
  const tryDemoBtn = document.getElementById('try-demo-btn');

  if (!tryDemoBtn) return;

  tryDemoBtn.addEventListener('click', () => {
    const demoSection = document.querySelector('[data-section="5"]');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

// ============================================================================
// FAQ ACCORDION
// ============================================================================

function initFAQ() {
  const faqQuestions = document.querySelectorAll('.faq-question');

  faqQuestions.forEach((question) => {
    question.addEventListener('click', () => {
      const faqItem = question.closest('.faq-item');
      const isOpen = faqItem.classList.contains('open');

      // Close all other items
      document.querySelectorAll('.faq-item.open').forEach((item) => {
        if (item !== faqItem) {
          item.classList.remove('open');
          item.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
        }
      });

      // Toggle current item
      if (isOpen) {
        faqItem.classList.remove('open');
        question.setAttribute('aria-expanded', 'false');
      } else {
        faqItem.classList.add('open');
        question.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

// ============================================================================
// BOOKING MODAL
// ============================================================================

function initBookingModal() {
  const bookingModal = document.getElementById('booking-modal');
  const bookingClose = document.getElementById('booking-close');
  const bookingTriggers = document.querySelectorAll('.booking-modal-trigger');
  const bookingFrame = bookingModal?.querySelector('iframe[data-booking-src]');
  let lastFocusedElement = null;

  if (!bookingModal || !bookingClose) {
    return;
  }

  const loadBookingCalendar = () => {
    if (bookingFrame && !bookingFrame.getAttribute('src')) {
      bookingFrame.setAttribute('src', bookingFrame.dataset.bookingSrc);

      if (!document.querySelector('script[data-ghl-booking-embed]')) {
        const embedScript = document.createElement('script');
        embedScript.src = 'https://link.flow-local.com/js/form_embed.js';
        embedScript.dataset.ghlBookingEmbed = 'true';
        document.body.appendChild(embedScript);
      }
    }
  };

  const openBookingModal = () => {
    lastFocusedElement = document.activeElement;

    bookingModal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    loadBookingCalendar();
    bookingClose.focus();
  };

  const closeBookingModal = () => {
    bookingModal.style.display = 'none';
    document.body.style.overflow = '';
    if (lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  };

  // Booking modal handlers
  bookingTriggers.forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      openBookingModal();
    });
  });

  bookingClose.addEventListener('click', closeBookingModal);

  // Close when clicking outside the modal
  bookingModal.addEventListener('click', (event) => {
    if (event.target === bookingModal) {
      closeBookingModal();
    }
  });

  // Close on escape key
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (bookingModal.style.display !== 'none') {
        closeBookingModal();
      }
    }
  });
}

// ============================================================================
// RESPONSIVE HANDLING
// ============================================================================

function handleResize() {
  if (prefersReducedMotion.matches) return;

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 250);
  });
}

// ============================================================================
// PERFORMANCE OPTIMIZATIONS
// ============================================================================

function initPerformanceOptimizations() {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  if (prefersReducedMotion.matches) {
    document.documentElement.classList.add('reduce-motion');
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function init() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    return;
  }

  console.log('🚀 Flow Local - Initializing...');

  initHeroCTA();
  initFAQ();
  initDemoPanel();
  initSmoothScroll();
  initDemoButtons();
  initBookingModal();
  handleResize();
  initPerformanceOptimizations();

  if (!prefersReducedMotion.matches) {
    initHeaderAnimation();
  }
  initScrollEffects();
  handleInitialAnchor();

  console.log('✅ Flow Local - Ready');
}

function handleInitialAnchor() {
  if (window.location.hash) {
    const scrollToTarget = () => {
      try {
        const target = document.querySelector(window.location.hash);
        if (target) {
          target.scrollIntoView();
          const section = target.classList.contains('section') ? target : target.closest('.section');
          if (section) {
            section.classList.add('section--visible');
            section.querySelectorAll('.feature').forEach(f => {
              f.style.opacity = '1';
              f.style.transform = 'none';
            });
          }
          if (typeof ScrollTrigger !== 'undefined') {
            ScrollTrigger.refresh();
          }
        }
      } catch (e) {}
    };

    scrollToTarget();
    window.addEventListener('load', scrollToTarget);
    setTimeout(scrollToTarget, 200);
  }
}

init();
